// dropdown-checkbox.component.ts
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnInit,
  Output,
  ViewChild,
  forwardRef,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import {
  debounceTime,
  distinctUntilChanged,
  map,
  startWith,
  tap,
} from 'rxjs/operators';

import { FormControl } from '@angular/forms';
import { FormOption } from 'src/app/core/services/forms.service';
import { LoggerService } from '@services/logger.service';
import { Observable } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

export interface PagedResult<T> {
  items: T[];
  next_page_token?: string;
}

export type ItemsProvider = (
  term: string,
  opts?: { pageToken?: string },
) => Observable<PagedResult<FormOption>>;

@Component({
  selector: 'app-dropdown-checkbox',
  templateUrl: './dropdown-checkbox.component.html',
  styleUrls: ['./dropdown-checkbox.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DropdownCheckboxComponent),
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class DropdownCheckboxComponent
  implements OnInit, AfterViewInit, ControlValueAccessor
{
  @Input() options$: Observable<FormOption[]>;
  @Input() options: FormOption[] = [];
  @Input() serverFiltering = false;
  @Input() resolveOptionsByValue: (ids: any[]) => Observable<FormOption[]>;
  @Input() hasMore$?: Observable<boolean>;
  @Input() loadMore?: () => void;
  @Output() searchChanged = new EventEmitter<string>();

  @ViewChild('displayContainer', { static: false })
  displayContainer: ElementRef;
  @ViewChild('filterInput') filterInput!: ElementRef<HTMLInputElement>;
  @ViewChild('menuContainer', { static: false })
  menuContainer!: ElementRef<HTMLDivElement>;

  // Header chips
  visibleLabels: { name: string; image?: string }[] = [];
  hiddenCount = 0;

  ngAfterViewInit() {
    this.updateVisibleLabels();
  }

  @HostListener('window:resize')
  onResize() {
    this.updateVisibleLabels();
  }

  displayedOptions: FormOption[] = [];
  filterControl = new FormControl('');
  isOpen = false;
  loading = false;
  disabled = false;
  filterText = '';

  // current value (array of IDs)
  value: any[] = [];

  private onChange = (_: any) => {};
  private onTouched = () => {};

  hasMore = false;
  loadingMore = false;

  protected destroyRef = inject(DestroyRef);
  protected translateService = inject(TranslateService);
  protected logger = inject(LoggerService);
  protected cdr = inject(ChangeDetectorRef);

  // Persistent cache of any option we've ever seen or resolved
  private knownOptions = new Map<string, FormOption>();
  // Fast, normalized set for checked state
  private valueSet = new Set<string>();

  constructor(private _eref: ElementRef) {}

  // Normalize/keys so 1 vs "1" doesn’t break equality
  private keyify(val: any): string {
    return String(val);
  }
  private normalize(val: any): any {
    return typeof val === 'string' && /^\d+$/.test(val) ? Number(val) : val;
  }
  private syncValueSet() {
    this.valueSet = new Set(this.value.map((v) => this.keyify(v)));
  }

  ngOnInit() {
    if (this.options$ !== undefined) {
      this.loading = true;
      this.options$
        .pipe(
          tap((opts) => {
            // Update current page of options
            this.options = opts;
            this.displayedOptions = opts;
            // Merge into persistent cache so header can show past selections
            for (const o of opts) {
              this.knownOptions.set(this.keyify(o.value), o);
            }

            this.loading = false;
            this.updateVisibleLabels();
          }),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe();
    }

    this.filterControl.valueChanges
      .pipe(
        startWith(''),
        map((text) => (text ?? '').trim().toLowerCase()),
        debounceTime(300),
        distinctUntilChanged(),
        tap((text) => {
          this.filterText = text;
          if (this.serverFiltering) {
            // Let parent fetch the new page
            this.searchChanged.emit(text);
          } else {
            // Client-side filter
            this.displayedOptions =
              text === ''
                ? this.options
                : this.options.filter((o) =>
                    (o.name || '').toLowerCase().includes(text),
                  );
          }
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    if (this.hasMore$) {
      this.hasMore$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((v) => {
        this.hasMore = v;
        this.cdr.markForCheck();
      });
    }
  }

  onMenuScroll() {
    if (!this.menuContainer || !this.hasMore || !this.loadMore) return;
    const el = this.menuContainer.nativeElement;
    const threshold = 80; // px from bottom to trigger
    const nearBottom =
      el.scrollTop + el.clientHeight >= el.scrollHeight - threshold;
    if (nearBottom) {
      this.loadMore();
    }
  }

  // Use persistent cache so labels don't disappear when server-filtering
  private updateVisibleLabels() {
    if (!this.displayContainer || !this.value) return;

    const container = this.displayContainer.nativeElement as HTMLElement;
    const containerWidth = container.offsetWidth;

    const selectedOptions: FormOption[] = this.value
      .map((v) => this.knownOptions.get(this.keyify(v)))
      .filter(Boolean) as FormOption[];

    let totalWidth = 0;
    const maxWidth = Math.max(0, containerWidth - 50);
    const visible: { name: string; image?: string }[] = [];

    const tempSpan = document.createElement('span');
    tempSpan.style.visibility = 'hidden';
    tempSpan.style.position = 'absolute';
    tempSpan.style.whiteSpace = 'nowrap';
    tempSpan.className = 'label-item';
    document.body.appendChild(tempSpan);

    for (const label of selectedOptions) {
      tempSpan.innerText = label.name;
      const labelWidth = tempSpan.offsetWidth + 8;
      if (totalWidth + labelWidth <= maxWidth) {
        totalWidth += labelWidth;
        visible.push({ name: label.name, image: (label as any).image });
      } else {
        break;
      }
    }

    this.visibleLabels = visible;
    this.hiddenCount = selectedOptions.length - visible.length;

    document.body.removeChild(tempSpan);
  }

  // ControlValueAccessor
  writeValue(obj: any[]): void {
    this.logger.debug('DropdownCheckboxComponent writeValue', obj);
    this.value = (obj || []).map((v) => this.normalize(v));
    this.syncValueSet();

    if (!this.options) this.options = [];

    // Ensure header can render names for any selected-but-not-loaded IDs
    if (this.resolveOptionsByValue && this.value.length > 0) {
      const missingKeys = this.value
        .map((v) => this.keyify(v))
        .filter((k) => !this.knownOptions.has(k));

      if (missingKeys.length > 0) {
        this.resolveOptionsByValue(missingKeys)
          .pipe(
            tap((resolved) => {
              for (const o of resolved) {
                this.knownOptions.set(this.keyify(o.value), o);
              }
              this.updateVisibleLabels();
              this.cdr.markForCheck();
            }),
            takeUntilDestroyed(this.destroyRef),
          )
          .subscribe();
      }
    }

    this.cdr.detectChanges();
  }
  registerOnChange(fn: any): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }
  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  get selectedCount(): number {
    return this.value.length;
  }

  toggleDropdown() {
    if (!this.loading && !this.disabled) {
      this.isOpen = !this.isOpen;
      if (this.isOpen) {
        this.onTouched();
        setTimeout(() => this.filterInput?.nativeElement?.focus(), 0);
      }
    }
  }

  onToggleAll(checked: boolean) {
    this.value = checked
      ? this.options.map((o) => this.normalize(o.value))
      : [];
    this.commitValueChange();
  }

  onToggleOption(opt: FormOption, checked: boolean) {
    const converted = this.normalize(opt.value);

    if (checked) {
      if (!this.value.some((v) => this.keyify(v) === this.keyify(converted))) {
        this.value = [...this.value, converted];
      }
      // Make sure selected option is cached for header
      this.knownOptions.set(this.keyify(opt.value), opt);
    } else {
      this.value = this.value.filter(
        (v) => this.keyify(v) !== this.keyify(converted),
      );
    }

    this.commitValueChange();
  }

  private commitValueChange() {
    this.syncValueSet();
    this.logger.debug('DropdownCheckboxComponent value change', this.value);
    this.onChange(this.value);
    this.onTouched();
    this.updateVisibleLabels();
  }

  allSelected(): boolean {
    return (
      this.options.length > 0 &&
      this.valueSet.size > 0 &&
      this.options.every((o) => this.valueSet.has(this.keyify(o.value)))
    );
  }

  // Helper used in template to avoid 1 vs "1" issues
  isSelected(opt: FormOption): boolean {
    return this.valueSet.has(this.keyify(opt.value));
  }

  @HostListener('document:click', ['$event'])
  clickOutside(event: Event) {
    if (!this._eref.nativeElement.contains(event.target)) {
      this.isOpen = false;
    }
  }
}
